/**
 * Provider Comparison API Service
 * Uses Next.js API routes (client-side safe)
 */

import type { ProviderComparisonRequest, ProviderComparisonResponse } from "@/types/provider";
import type { BackendProviderComparisonResponse } from "@/types/backend";
import { extractNestedData } from "@/lib/utils/response-parser";

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  error?: any;
}

export const providerService = {
  /**
   * Compare providers for a currency exchange
   * Uses Next.js API route for client-side safety
   */
  async compare(request: ProviderComparisonRequest): Promise<ProviderComparisonResponse["data"]> {
    const response = await fetch("/api/providers/compare", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const error: ApiResponse<never> = await response.json().catch(() => ({
        success: false,
        message: "Failed to compare providers",
      }));
      throw new Error(error.message || "Failed to compare providers");
    }

    const result: ApiResponse<ProviderComparisonResponse["data"]> = await response.json();

    if (!result.success || !result.data) {
      throw new Error(result.message || "Failed to compare providers");
    }

    // Extract and map backend response to expected format
    const backendData = extractNestedData<BackendProviderComparisonResponse>(result.data);

    if (!backendData) {
      throw new Error("Invalid provider comparison response structure");
    }

    const wise = Array.isArray(backendData.wise_comparison)
      ? backendData.wise_comparison.map((p) => ({
          name: p.name ?? p.alias ?? "Unknown provider",
          logos: p.logos,
          quotes: p.quotes?.map((q) => ({
            receivedAmount: q.receivedAmount ?? 0,
            rate: q.rate ?? 0,
            fee: q.fee ?? 0,
            markup: q.markup,
            markup_fee: q.markup_fee,
            total_fee: q.total_fee,
          })),
        }))
      : [];

    return {
      from_currency: backendData.from_currency ?? backendData.from ?? "AUD",
      from_currency_symbol: backendData.from_currency_symbol,
      to_currency: backendData.to_currency ?? backendData.to,
      to_currency_symbol: backendData.to_currency_symbol,
      base_amount: backendData.base_amount,
      converted_amount: backendData.converted_amount ?? 0,
      rate: backendData.rate ?? 0,
      fee: backendData.fee ?? 0,
      wise_comparison: wise,
    };
  },
};
